import { defineComponent as _defineComponent } from 'vue'
import licenseIcon from '@/assets/license.svg';
import { defineMessages } from '@/lib/i18n';
import { nonNullable } from '@/utils/nonNullable';
import TablerExternalLinkIcon from '@/components/icons/TablerExternalLinkIcon.vue';
import TablerScaleIcon from '@/components/icons/TablerScaleIcon.vue';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { usePermissionComposable } from '@/composables/usePermission';
import Section8ExposureControl from '@/views/dashboard/sdsDrawer/section8/Section8ExposureControl.vue';
import {
  PropType,
  computed,
  onBeforeMount,
  onMounted,
  reactive,
  ref,
  watch,
  watchEffect,
} from 'vue';
import { DynamicList } from '@/interfaces/api/general/dynamicList';
import {
  SdsEditHistoryResponse,
  SdsSection8ExposureControl,
} from '@/interfaces/model/general/sds';
import { FormItem } from '@/interfaces/lib/model';
import { Department } from '@/interfaces/model/general/department';
import {
  Sds,
  AssistantLogCrossCheck,
  SdsDynamicListResult,
  SdsProduct,
  SdsSection15,
  SdsSection1Importer,
  SdsSection1Manufacturer,
  SdsSection2,
  SdsSection2Label,
  SdsSection3,
  SdsSection8,
  SdsSection9,
  SdsSection9BoilingPoint,
  SdsSection9FlashPoint,
  SdsEditHistory,
  SdsEditHistoryDetail,
} from '@/interfaces/model/general/sds';
import { UserReanalysisComment } from '@/interfaces/model/general/user_reanalysis_comment';
import { User } from '@/interfaces/vuex/user';
import { getPdfDisplayOptions } from '@/lib/formItem';
import { StatusId, RevisionStatus } from '@/lib/enum';
import { LanguageId } from '@/lib/enum/LanguageId';
import {
  formatToDateTime,
  fromISO,
  latest,
  now,
  formatDatePickerDay,
} from '@/utils/datetime';

import PdfViewer from '@/views/dashboard/PdfViewer.vue';
import ReAnalysisSds from '@/views/dashboard/sdsDrawer/ReAnalysisSds.vue';
import SupplierInquiryRequestButton from '@/views/dashboard/sdsDrawer/SupplierInquiryRequestButton.vue';
import { generalApi } from '@/lib/api/general';
import { SupplierInquiryResponse } from '@/interfaces/api/general/supplierInquiry';
import SdsTableSectionDefault from '@/views/dashboard/sdsDrawer/SdsTableSectionDefault.vue';
import SdsTableSectionAccordionDefault from '@/views/dashboard/sdsDrawer/SdsTableSectionAccordionDefault.vue';
import SdsTableSection0 from '@/views/dashboard/sdsDrawer/SdsTableSection0.vue';
import SdsTableSection0DynamicList from '@/views/dashboard/sdsDrawer/SdsTableSection0DynamicList.vue';
import SdsTableSection1ProductInfo from '@/views/dashboard/sdsDrawer/SdsTableSection1ProductInfo.vue';
import SdsTableSection14 from '@/views/dashboard/sdsDrawer/SdsTableSection14.vue';
import SdsTableSection15 from '@/views/dashboard/sdsDrawer/SdsTableSection15.vue';
import SdsTableSection15Material from '@/views/dashboard/sdsDrawer/SdsTableSection15Material.vue';
import SdsTableSection1Importer from '@/views/dashboard/sdsDrawer/SdsTableSection1Importer.vue';
import SdsTableSection1Manufacturer from '@/views/dashboard/sdsDrawer/SdsTableSection1Manufacturer.vue';
import SdsTableSection2Ghs from '@/views/dashboard/sdsDrawer/SdsTableSection2GHS.vue';
import SdsTableSection2Label from '@/views/dashboard/sdsDrawer/SdsTableSection2Label.vue';
import SdsTableSection3 from '@/views/dashboard/sdsDrawer/SdsTableSection3.vue';
import SdsTableSection3Material from '@/views/dashboard/sdsDrawer/SdsTableSection3Material.vue';
import SdsTableSection3Impurity from '@/views/dashboard/sdsDrawer/SdsTableSection3Impurity.vue';
import SdsTableSection8 from '@/views/dashboard/sdsDrawer/section8/SdsTableSection8.vue';
import SdsTableSection9 from '@/views/dashboard/sdsDrawer/SdsTableSection9.vue';
import SdsTableSection9BoilingPoint from '@/views/dashboard/sdsDrawer/SdsTableSection9BoilingPoint.vue';
import SdsTableSection9FlashPoint from '@/views/dashboard/sdsDrawer/SdsTableSection9FlashPoint.vue';
import SdsTableSectionAccordion from '@/views/dashboard/sdsDrawer/SdsTableSectionAccordion.vue';
import SdsTableEditHistory from '@/views/dashboard/sdsDrawer/SdsTableEditHistory.vue';
import SdsGhsLabelSimpleDialog from '@/views/dashboard/sdsDrawer/SdsGhsLabelSimpleDialog.vue';
import { TagResponse } from '@/interfaces/model/general/tag';
import { Attachment } from '@/interfaces/model/general/attachment';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { readAssertedCompany, readUserLocale } from '@/store/main/getters';
import { commitAddNotification } from '@/store/main/mutations';
import { store } from '@/store/index';
import { formatToDate } from '@/utils/datetime';
import { riskAssessmentBaseUrl } from '@/env';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableDrawer',
  props: {
  activeSds: {
    type: Object as () => Sds,
    required: true,
  },
  productFormItems: {
    type: Array as PropType<Array<FormItem>>,
    value: () => [],
    required: true,
  },
  materialFormItems: {
    type: Array as PropType<Array<FormItem>>,
    value: () => [],
    required: true,
  },
  casFormItems: {
    type: Array as PropType<Array<FormItem>>,
    value: () => [],
    required: true,
  },
  statusList: {
    type: Array as PropType<Array<FormItem>>,
    default: () => [],
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
    required: true,
  },
  isLoadingSds: {
    type: Boolean,
    default: false,
    required: true,
  },
  showSdsDetailView: {
    type: Boolean,
    default: false,
    required: true,
  },
  pdf: {
    type: String,
    default: '',
    required: true,
  },
  uuid: {
    type: String,
    default: '',
    // required: true, // TODO: 利用箇所で設定されていない場合があったので、コメントアウト
  },
  name: {
    type: String,
    default: '',
  },
  publishedAt: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
  revisedAt: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
  createdAt: {
    type: String,
    required: true,
  },
  updatedAt: {
    type: String,
    required: true,
  },
  uploadUser: {
    type: Object as PropType<User | null>,
    default: null,
  },
  assigneeUsers: {
    type: Array as PropType<Array<User>>,
    default: () => [],
  },
  companyUsers: {
    type: Array as PropType<User[]>,
    default: () => [],
  },
  supplierConfirmedAt: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
  assessedAt: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
  statusId: {
    type: Number,
    required: true,
  },
  adminNote: {
    type: String,
    required: true,
  },
  note: {
    type: String,
    required: true,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
    required: true,
  },
  sdsSection8: {
    type: Object as PropType<SdsSection8>,
    default: () => {
      return {
        id: 0,
        sds_id: '',
        skin_protection: '',
        protective_equipment_of_skin: 'NONE',
      };
    },
  },
  unNumber: {
    type: String,
    required: true,
  },
  unNumberList: {
    type: Array as PropType<Array<string>>,
    required: true,
  },
  hasPrevSds: {
    type: Boolean,
  },
  hasNextSds: {
    type: Boolean,
  },
  width: {
    type: String,
    default: '70%',
    required: false,
  },
  canExportCreateSimpleCount: {
    type: Number,
    default: 0,
    required: false,
  },
  canExportCreateSimpleCountV3: {
    type: Number,
    default: 0,
    required: false,
  },
  dynamicLists: {
    type: Array as PropType<Array<DynamicList>>,
    // required: true, // TODO: 利用箇所で設定されていない場合があったので、コメントアウト
    default: () => [],
  },
  dynamicListResult: {
    type: Array as PropType<Array<SdsDynamicListResult>>,
    // required: true, // TODO: 利用箇所で設定されていない場合があったので、コメントアウト
    default: () => [],
  },
  assistantLogCrossChecks: {
    type: Array as PropType<Array<AssistantLogCrossCheck>>,
    // required: true, // TODO: 利用箇所で設定されていない場合があったので、コメントアウト
    default: () => [],
  },
  userReanalysisComment: {
    type: Array as PropType<Array<UserReanalysisComment>>,
    default: () => [],
  },
  attachments: {
    type: Array as PropType<Array<Attachment>>,
    default: () => [],
  },
  productCodeList: {
    type: Array as PropType<Array<string>>,
    default: () => [],
  },
  fileTags: {
    type: Array as PropType<Array<TagResponse>>,
    // required: true, // TODO: 利用箇所で設定されていない場合があったので、コメントアウト
    default: () => [],
  },
  departments: {
    type: Array as PropType<Array<Department>>,
    // required: true, // TODO: 利用箇所で設定されていない場合があったので、コメントアウト
    default: () => [],
  },
  companyDepartments: {
    type: Array as PropType<Array<Department>>,
    required: true,
  },
  statusOptions: {
    type: Array as PropType<{ value: string; state: string }[]>,
    required: false,
    default: () => [],
  },
  sdsEditHistories: {
    type: Array as PropType<Array<SdsEditHistoryResponse>>,
    default: () => [],
  },
},
  emits: ["update:isValid", "update:isEditing", "showPrevSds", "showNextSds", "update:showSdsDetailView", "update:productFormItems", "update:materialFormItems", "update:casFormItems", "update:name", "update:publishedAt", "update:revisedAt", "update:assessedAt", "update:statusId", "update:note", "update:pdf", "update:attachments", "update:assigneeUsers", "update:departments", "updateCompanySds", "update:products", "update:sdsSection8", "update:unNumber", "update:unNumberList", "update:productCodeList", "update:fileTags", "cancelEdit", "editDynamicListResult", "addDynamicListResult", "deleteDynamicListResult", "exportCreateSimple", "exportCreateSimpleV3", "exportCreateSimpleV3_2"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', bool: boolean): void;
  (e: 'update:isEditing', bool: boolean): void;
  (e: 'showPrevSds'): void;
  (e: 'showNextSds'): void;
  (e: 'update:showSdsDetailView', bool: boolean): void;
  (e: 'update:productFormItems', data: Array<FormItem>): void;
  (e: 'update:materialFormItems', data: Array<FormItem>): void;
  (e: 'update:casFormItems', data: Array<FormItem>): void;
  (e: 'update:name', d: string): void;
  (e: 'update:publishedAt', d: string | null): void;
  (e: 'update:revisedAt', d: string | null): void;
  (e: 'update:assessedAt', d: string | null): void;
  (e: 'update:statusId', d: number): void;
  (e: 'update:note', d: string): void;
  (e: 'update:pdf', d: string): void;
  (e: 'update:attachments', d: Array<Attachment>): void;
  (e: 'update:assigneeUsers', users: Array<User>): void;
  (e: 'update:departments', departments: Department[]): void;
  (e: 'updateCompanySds', tags: TagResponse[], departments: Department[]): void;
  (e: 'update:products', d: Array<SdsProduct>): void;
  (e: 'update:sdsSection8', data: SdsSection8): void;
  (e: 'update:unNumber', data: string): void;
  (e: 'update:unNumberList', data: Array<string>): void;
  (e: 'update:productCodeList', data: Array<string>): void;
  (e: 'update:fileTags', data: Array<TagResponse>): void;
  (e: 'cancelEdit'): void;
  (
    e: 'editDynamicListResult',
    sdsId: string,
    dynamicListId: number,
    dynamicListResult: SdsDynamicListResult
  ): void;
  (e: 'addDynamicListResult', sdsId: string, dynamicListId: number): void;
  (e: 'deleteDynamicListResult', sdsId: string, dynamicListId: number): void;
  (
    e: 'editDynamicListResult',
    sdsId: string,
    dynamicListId: string,
    dynamicListResult: SdsDynamicListResult
  ): void;
  (e: 'exportCreateSimple', sdsId: string): void;
  (e: 'exportCreateSimpleV3', sdsId: string): void;
  (e: 'exportCreateSimpleV3_2', sdsId: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);
const locale = computed(() => readUserLocale(store));
const pdfDisplayOptions = getPdfDisplayOptions();
const company = reactive({ ...readAssertedCompany(store) });

const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
  useCompanyFeatureComposable();

const isSDSDigitizationCompleted = computed(
  () => props.statusId === StatusId.SECOND_CHECK_DONE.value
);

const isRiskAssessmentActive = computed(() =>
  isFeatureActive(COMPANY_FEATURES.CHEMICAN_RISK_ASSESSMENT)
);

const m = defineMessages({
  loadingData: 'データを読み込んでいます',
  save: '保存',
  sdsInfo: 'SDS情報',
  checkItems: 'チェック項目',
  editHistory: '履歴',
  basicInfo: '0. 基礎情報',
  section1ProductInfo: '1. 化学品及び会社情報(製品情報)',
  chemicalAndCompanyInfoManufacturer: '1. 化学品及び会社情報(製造者)',
  chemicalAndCompanyInfoImporter: '1. 化学品及び会社情報(輸入者・販売者)',
  summaryOfHazard: '2. 危険有害性の要約',
  ghsLabel: '2. GHSラベル',
  ghsLabelPrint: 'GHSラベル印刷',
  digitizationCompletedOnly:
    'データ化完了ステータスのSDSのみご利用いただけます',
  ghsLabelPrintNoPermission: 'ラベル出力にはダウンロード権限が必要です',
  compositionAndInformation: '3. 組成及び成分情報',
  exposureLimits: '8. 曝露限界値',
  physicalAndChemicalProperties: '9. 物理的及び化学的性質',
  transportationPrecautions: '14. 輸送上の注意',
  applicableLaws: '15. 適用法令',
  applicableLawsLabel: 'に関する適用法令一覧',
  applicableLawsLabelDefault: '製品に関する適用法令一覧',
  applicableLawsPerMaterialLabel: '含有成分に関する適用法令一覧',
  latest: '最新',
  revisionDate: '改訂日',
  viewDetails: '詳細を見る',
  selectProductToInput: '製品を選択すると入力できるようになります',
  copyUrl: 'URLをコピーする',
  companyNameManufacturer: '会社名（製造者）',
  possibleContains:
    '安衛法：ラベル表示・SDS交付の義務化対象物質（リスクアセスメント対象物質）を含む可能性のあるSDS',
  copiedUrl: 'URLをコピーしました',
  copyFailed: 'URLをコピーに失敗しました',
  versionInfo: 'バージョン情報',
  versionList: 'バージョン一覧',
});





const {
  hasPermission,
  hasSdsEditPermission,
  hasSdsRequestPermission,
  PERMISSIONS,
} = usePermissionComposable();

const hasGhsLabelPrintPermission = computed(() =>
  hasPermission(PERMISSIONS.LABEL_DOWNLOAD)
);

const updatableValuesNum = StatusId.updatableValues();
const canEdit = computed(() => {
  return updatableValuesNum.includes(props.statusId);
});

const modalSupplierConfirmedAt = ref<boolean>(false);

const closeSupplierConfirmedAtModal = () => {
  modalSupplierConfirmedAt.value = false;
};

const isValidSection1Manufacturers = ref<Array<Array<boolean>>>([]);
const isValidSection1Importers = ref<Array<Array<boolean>>>([]);
const isValidSection2Ghs = ref<Array<boolean>>([]);
const isValidSection2Label = ref<Array<boolean>>([]);
const isValidSection3 = ref<Array<Array<boolean>>>([]);
const isValidSection3Impurity = ref<Array<Array<boolean>>>([]);
const isValidSection8 = ref<boolean>(false);
const isValidSection8ExposureControl = ref<Array<Array<boolean>>>([]);
const isValidSection9 = ref<Array<Array<boolean>>>([]);
const isValidSection9NewFlashPoint = ref<boolean>(false);
const isValidSection9NewBoilingPoint = ref<boolean>(false);
const isValidSection14 = ref<boolean>(false);
const isValidSection15 = ref<Array<Array<boolean>>>([]);
const selectedPdfDisplayDetailViewOption =
  ref<(typeof pdfDisplayOptions)[0]['value']>('default');
const fileTags = ref<TagResponse[]>([
  ...props.fileTags.map((t) => ({ id: t.id, name: t.name })),
]);
const productIndex = ref<number>(0);
const currentProductId = computed(
  () => props.products[productIndex.value]?.id ?? null
);

const riskAssessmentUrl = computed(() => {
  if (!isRiskAssessmentActive.value) return '';

  const companyId = props.activeSds.company_id;
  const sdsId = props.activeSds.uuid;
  const productId = props.products[productIndex.value]?.id;
  return `${riskAssessmentBaseUrl}/${companyId}/risk-assessments/${sdsId}/${productId}`;
});

const textToCopy = ref<string>('');
const tooltipMessage = ref<string>($t(m.copyUrl));
const isUploading = ref<boolean>(false);
const showGhsLabelPrintDialog = ref<boolean>(false);
const onGhsLabelSuccess = (message: string) => {
  commitAddNotification(store, { content: message, color: 'success' });
};

const generateUrl = (uuid): string => {
  const protocol = window.location.protocol;
  const host = window.location.host;
  const url = `${protocol}//${host}/#/dashboard/sds/index?uuid=${uuid}`;
  return url;
};

const editHistories = computed(() => {
  const { companyUsers, products } = props;
  const userMap = new Map(companyUsers.map((user) => [user.uuid, user]));
  const productMap = new Map(products.map((product) => [product.id, product]));

  const histories = props.sdsEditHistories;
  histories.sort(
    (a, b) => fromISO(b.edited_at).valueOf() - fromISO(a.edited_at).valueOf()
  );
  return {
    editHistories: histories
      .filter((history) => {
        return (
          userMap.has(history.user_id) &&
          history.details.every((detail) => {
            return (
              !detail.sds_product_id || productMap.has(detail.sds_product_id)
            );
          })
        );
      })
      .map((history): SdsEditHistory => {
        const details = history.details.map((detail): SdsEditHistoryDetail => {
          const sdsProduct = detail.sds_product_id
            ? productMap.get(detail.sds_product_id)
            : null;
          return {
            ...detail,
            sds_product: !sdsProduct
              ? null
              : {
                  id: sdsProduct.id,
                  name: sdsProduct.name,
                },
          };
        });
        const user = userMap.get(history.user_id);
        return {
          uuid: history.uuid,
          sds_id: history.sds_id,
          edited_at: history.edited_at,
          created_at: history.created_at,
          updated_at: history.updated_at,
          user: {
            uuid: user?.uuid ?? '',
            first_name: user?.first_name ?? '',
            last_name: user?.last_name ?? '',
            middle_name: user?.middle_name ?? '',
          },
          details,
        };
      }),
  };
});

const checkProps = computed(() => {
  const {
    revision_status,
    sds_products,
    revised_at: activeSdsRevisedAt,
    same_name_sds_info,
    supplier_confirmed_at,
  } = props.activeSds;
  const latestRevisedAt =
    activeSdsRevisedAt > same_name_sds_info[0]?.revised_at
      ? activeSdsRevisedAt
      : same_name_sds_info[0]?.revised_at;
  return {
    product_names: {
      title: $t(sharedMessages.productName),
      type: 'list',
      value: sds_products.map((product) => product.name),
    },
    manufacturer_names: {
      title: $t(m.companyNameManufacturer),
      value: sds_products?.reduce(
        (manufacturers, product) => [
          ...manufacturers,
          ...(product?.sds_section1_manufacturers || []).map(
            (manufacturer) => manufacturer.name
          ),
        ],
        []
      ),
    },
    revision_date: {
      title: $t(m.revisionDate),
      value: formatToDate(activeSdsRevisedAt),
    },
    supplier_confirmed_at: {
      title: $t(sharedMessages.supplierConfirmedAt),
      value: formatToDate(supplier_confirmed_at),
    },
    revision_status: {
      title: $t(sharedMessages.revisionStatus),
      type: 'revision_status',
      value: RevisionStatus[revision_status],
    },
    update_infos: {
      title: $t(m.versionList),
      type: 'table',
      value: same_name_sds_info.map(
        ({
          name,
          revised_at,
          uuid,
          /* TODO: put revision_status here after revision_group_uuid is implemented */
        }) => ({
          name,
          revised_at,
          url: generateUrl(uuid),
          isLatest: revised_at === latestRevisedAt,
        })
      ),
    },
    alerts: [],
    check_list: [
      {
        name: $t(m.possibleContains),
        is_applicable: true,
        factor: '-',
        memo: '-',
      },
    ],
  };
});

const section15WithoutMaterialList = computed(() => {
  return (currentSection15.value || []).filter(
    (s15) => !Array.isArray(s15.material_list) || s15.material_list.length === 0
  );
});
const section15WithMaterialList = computed(() => {
  return (currentSection15.value || []).filter(
    (s15) => Array.isArray(s15.material_list) && s15.material_list.length > 0
  );
});

const copyText = async () => {
  try {
    await navigator.clipboard.writeText(textToCopy.value);
    tooltipMessage.value = $t(m.copiedUrl);
    setTimeout(() => {
      tooltipMessage.value = $t(m.copyUrl);
    }, 2000);
  } catch (err) {
    tooltipMessage.value = $t(m.copyFailed);
  }
};

const startEdit = () => {
  updateIsEditing(true);
};

// 保存完了時とキャンセル時で挙動を変える。キャンセルをクリックしたら入力前に戻るようにする
const cancelEdit = () => {
  emits('cancelEdit');
  updateIsEditing(false);
};

const endEdit = () => {
  updateIsEditing(false);
};

const currentSection15 = computed((): Array<SdsSection15> => {
  return props.products[productIndex.value]?.sds_section15_list;
});

// Computed property for section 3 material list (for section 15 component)
const section3MaterialList = computed(() => {
  if (!props.products.length || productIndex.value >= props.products.length) {
    return [];
  }
  const product = props.products[productIndex.value];
  if (!product || !product.sds_section3) {
    return [];
  }
  const materialList = (product.sds_section3.material_list ?? []).map((m) => ({
    ...m,
    is_impurity: false,
  }));
  const impurityList = (product.sds_section3.impurity_list ?? []).map((m) => ({
    ...m,
    is_impurity: true,
  }));
  return [...materialList, ...impurityList];
});

// Check if SDS language is Chinese
const isSdsChinese = computed((): boolean => {
  return props.activeSds.sds_type?.language_id === LanguageId.CHINESE.value;
});

// Check if SDS revision date is older than 5 years
const isSdsOlderThan5Years = computed((): boolean => {
  if (!props.activeSds.revised_at) {
    return false;
  }
  const revisedDate = fromISO(props.activeSds.revised_at);
  const fiveYearsAgo = now().minus({ years: 5 });
  return revisedDate < fiveYearsAgo;
});

// Check if any section 8+ is empty
const hasAnyEmptySection8Plus = computed((): boolean => {
  return (
    isSection8Empty.value ||
    isSection9Empty.value ||
    isSection14Empty.value ||
    isSection15Empty.value
  );
});

// Check if section 8 is empty
const isSection8Empty = computed((): boolean => {
  if (!props.products.length || productIndex.value >= props.products.length)
    return false;
  const hasSection8 =
    props.sdsSection8?.protective_equipment_of_skin &&
    props.sdsSection8.protective_equipment_of_skin !== 'NONE';
  return !hasSection8;
});

// Check if section 9 is empty
const isSection9Empty = computed((): boolean => {
  if (!props.products.length || productIndex.value >= props.products.length)
    return false;
  const product = props.products[productIndex.value];
  if (!product) return false;
  const hasSection9 = (product.sds_section9_list || []).length > 0;
  const hasFlashPoint =
    (product.sds_section9_flash_point_list || []).length > 0;
  const hasBoilingPoint =
    (product.sds_section9_boiling_point_list || []).length > 0;
  return !hasSection9 && !hasFlashPoint && !hasBoilingPoint;
});

// Check if section 14 is empty
const isSection14Empty = computed((): boolean => {
  return (
    !props.unNumber && (!props.unNumberList || props.unNumberList.length === 0)
  );
});

// Check if section 15 is empty
const isSection15Empty = computed((): boolean => {
  if (!props.products.length || productIndex.value >= props.products.length)
    return false;
  return (section15WithoutMaterialList.value || []).length === 0;
});

const updateName = (e: string) => {
  emits('update:name', e);
};

const updateSupplierConfirmedAt = (e: string | null) => {
  emits('update:supplierConfirmedAt', e);
};

const updateAssessedAt = (e: string | null) => {
  emits('update:assessedAt', e);
};

const showPrevSds = () => {
  emits('showPrevSds');
};

const showNextSds = () => {
  emits('showNextSds');
};

const closeSdsDetail = () => {
  emits('update:showSdsDetailView', false);

  updateIsEditing(false);
};

const updateProductFormItems = (data: Array<FormItem>): void => {
  emits('update:productFormItems', data);
};

const updateAttachments = (data: Array<Attachment>): void => {
  emits('update:attachments', data);
};

const updateProducts = (data: Array<SdsProduct>): void => {
  emits('update:products', data);
};

const updateSection1Manufacturers = (e: Array<SdsSection1Manufacturer>) => {
  const updatedData = structuredClone(props.products);
  updatedData[productIndex.value].sds_section1_manufacturers = e;

  emits('update:products', updatedData);
};

const updateSection1Importers = (e: Array<SdsSection1Importer>) => {
  const updatedData = structuredClone(props.products);
  updatedData[productIndex.value].sds_section1_importers = e;

  emits('update:products', updatedData);
};

const updateSection2 = (e: Array<SdsSection2>) => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section2_list = e;

  emits('update:products', updatedData);
};

const updateSection2Label = (e: SdsSection2Label) => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section2_label = e;

  emits('update:products', updatedData);
};

const updateSection3 = (e: SdsSection3) => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section3 = e;

  emits('update:products', updatedData);
};

const updateSection8 = (e: SdsSection8) => {
  const updatedData = structuredClone(props.sdsSection8);
  updatedData.protective_equipment_of_skin = e.protective_equipment_of_skin;
  updatedData.skin_protection =
    e.protective_equipment_of_skin == 'REQUIRED' ? 'あり' : '-';

  emits('update:sdsSection8', updatedData);
};

const updateSection8ExposureControl = (data: SdsSection8ExposureControl[]) => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section8_exposure_control_list = data;
  emits('update:products', updatedData);
};

const updateSection9 = (e: Array<SdsSection9>) => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section9_list = e;

  emits('update:products', updatedData);
};

const updateSection9FlashPoint = (e: Array<SdsSection9FlashPoint>): void => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section9_flash_point_list = e;

  emits('update:products', updatedData);
};

const updateSection9BoilingPoint = (
  e: Array<SdsSection9BoilingPoint>
): void => {
  const updatedData = [...props.products];
  updatedData[productIndex.value].sds_section9_boiling_point_list = e;

  emits('update:products', updatedData);
};

const updateUnNumber = (e: string) => {
  emits('update:unNumber', e);
};

const updateUnNumberList = (e: Array<string>) => {
  emits('update:unNumberList', e);
};

const updateSection15WithoutMaterialList = (e: Array<SdsSection15>) => {
  const updatedData = [...props.products];
  const productToUpdate = { ...updatedData[productIndex.value] };

  // Keep items that have a non-empty material_list
  const withMaterials = (productToUpdate.sds_section15_list || []).filter(
    (s15) => Array.isArray(s15.material_list) && s15.material_list.length > 0
  );
  // Combine with new items (those without materials)
  productToUpdate.sds_section15_list = [...withMaterials, ...e];
  updatedData[productIndex.value] = productToUpdate;
  emits('update:products', updatedData);
};

const updateSection15WithMaterialList = (e: Array<SdsSection15>) => {
  const updatedData = [...props.products];
  const productToUpdate = { ...updatedData[productIndex.value] };

  // Keep items that have NO material_list or an empty one
  const withoutMaterials = (productToUpdate.sds_section15_list || []).filter(
    (s15) => !Array.isArray(s15.material_list) || s15.material_list.length === 0
  );
  // Combine with new items (those with materials)

  // Filter out SdsSection15 where all law_id, regulation1_id-5_id, and text are empty/falsy
  const filteredE = e.filter((s15) => {
    return !!(
      (s15.material_list && s15.material_list.length > 0) ||
      s15.law_id ||
      s15.regulation1_id ||
      s15.regulation2_id ||
      s15.regulation3_id ||
      s15.regulation4_id ||
      s15.regulation5_id ||
      (s15.text && s15.text.trim())
    );
  });
  productToUpdate.sds_section15_list = [...withoutMaterials, ...filteredE];
  updatedData[productIndex.value] = productToUpdate;
  emits('update:products', updatedData);
};

const resetProductIndex = () => {
  productIndex.value = 0;
};

const updateIsEditing = (e: boolean) => {
  emits('update:isEditing', e);
};

const updateCompanySds = (tags: TagResponse[], departments: Department[]) => {
  emits('updateCompanySds', tags, departments);
};

const updatePublishedAt = (e: string | null) => {
  emits('update:publishedAt', e);
};

const updateRevisedAt = (e: string | null) => {
  emits('update:revisedAt', e);
};

const updateNote = (e: string) => {
  emits('update:note', e);
};

const updatePdf = (e: string) => {
  emits('update:pdf', e);
};

const updateProductCodeList = (data: Array<string>) => {
  emits('update:productCodeList', data);
};

const updateFileTags = (e: Array<TagResponse>) => {
  emits('update:fileTags', e);
};

const updateAssigneeUsers = (e: User[]) => {
  emits('update:assigneeUsers', e);
};

const updateFileDepartments = (e: Array<Department>) => {
  emits('update:departments', e);
};

const exportCreateSimple = (sdsId: string) => {
  emits('exportCreateSimple', sdsId);
};

const exportCreateSimpleV3 = (sdsId: string) => {
  emits('exportCreateSimpleV3', sdsId);
};

const exportCreateSimpleV3_2 = (sdsId: string) => {
  emits('exportCreateSimpleV3_2', sdsId);
};

const editDynamicListResult = (
  sdsId: string,
  dynamicListId: number,
  dynamicListResult: SdsDynamicListResult
) => {
  emits('editDynamicListResult', sdsId, dynamicListId, dynamicListResult);
};

const addDynamicListResult = (sdsId: string, dynamicListId: number) => {
  emits('addDynamicListResult', sdsId, dynamicListId);
};

const deleteDynamicListResult = (sdsId: string, dynamicListId: number) => {
  emits('deleteDynamicListResult', sdsId, dynamicListId);
};

const pdpageScalefApp = computed((): string => {
  return selectedPdfDisplayDetailViewOption.value == 'max'
    ? 'page-width'
    : 'page-fit';
});

watchEffect((): void => {
  const isValid =
    isValidSection1Manufacturers.value.flat().every((value) => value == true) &&
    isValidSection1Importers.value.flat().every((value) => value == true) &&
    isValidSection2Ghs.value.flat().every((value) => value == true) &&
    isValidSection2Label.value.flat().every((value) => value == true) &&
    isValidSection3.value.flat().every((value) => value == true) &&
    isValidSection3Impurity.value.flat().every((value) => value == true) &&
    isValidSection8.value &&
    isValidSection8ExposureControl.value.flat().every((value) => value) &&
    isValidSection9.value.flat().every((value) => value == true) &&
    isValidSection14.value &&
    isValidSection15.value.flat().every((value) => value == true);
  emits('update:isValid', isValid);
});

const lastCrossCheckEndedAt = computed(() => {
  // First, try to get ended_at from assistant_log_cross_checks
  if (
    props.assistantLogCrossChecks &&
    props.assistantLogCrossChecks.length > 0
  ) {
    const endedAtList = props.assistantLogCrossChecks
      .filter((item) => !item.deleted_at)
      .map((item) => item.ended_at)
      .filter(nonNullable);
    if (endedAtList.length > 0) {
      const latestEndedAt = latest(...endedAtList.map((x) => fromISO(x)));
      if (latestEndedAt) {
        return latestEndedAt.toString();
      }
    }
  }

  // If no ended_at from assistant_log_cross_checks, check user_reanalysis_comment
  // Use the most recent reanalysis comment's updated_at as the completion date
  if (props.userReanalysisComment && props.userReanalysisComment.length > 0) {
    const reanalysisComments = props.userReanalysisComment
      .filter((item) => !item.deleted_at)
      .sort((a, b) => {
        const dateA = fromISO(a.updated_at);
        const dateB = fromISO(b.updated_at);
        if (!dateA || !dateB) return 0;
        // Compare DateTime objects using toMillis() or valueOf()
        return dateB.valueOf() - dateA.valueOf(); // Sort descending (most recent first)
      });

    if (reanalysisComments.length > 0 && reanalysisComments[0].updated_at) {
      return reanalysisComments[0].updated_at;
    }
  }

  // Fallback to updatedAt if status is SECOND_CHECK_DONE (reanalysis completed)
  if (props.statusId === StatusId.SECOND_CHECK_DONE.value) {
    return props.updatedAt;
  }

  return undefined;
});

const lastUpdatedAt = computed(() => {
  const updatedAtList: Array<string> = [];
  updatedAtList.push(props.updatedAt);
  for (const product of props.products) {
    updatedAtList.push(product.updated_at);
    updatedAtList.push(product.sds_section2_label.updated_at);
    updatedAtList.push(
      ...product.sds_section1_importers.map((x) => x.updated_at)
    );
    updatedAtList.push(
      ...product.sds_section1_manufacturers.map((x) => x.updated_at)
    );
    updatedAtList.push(...product.sds_section2_list.map((x) => x.updated_at));
    updatedAtList.push(...product.sds_section9_list.map((x) => x.updated_at));

    for (const material of product.sds_section3.material_list) {
      updatedAtList.push(...material.cas_list.map((x) => x.updated_at));
      updatedAtList.push(material.updated_at);
    }

    for (const section8 of product.sds_section8_exposure_control_list) {
      updatedAtList.push(...section8.material_list.map((x) => x.updated_at));
      updatedAtList.push(section8.updated_at);
    }

    for (const section15 of product.sds_section15_list) {
      updatedAtList.push(...section15.material_list.map((x) => x.updated_at));
      updatedAtList.push(section15.updated_at);
      for (const material of section15.material_list) {
        updatedAtList.push(material.updated_at);
        updatedAtList.push(...material.cas_list.map((x) => x.updated_at));
      }
    }
  }
  updatedAtList.push(...props.sdsSection8.updated_at);
  const maxUpdatedAt = latest(...updatedAtList.map((x) => fromISO(x)));

  return formatToDateTime(maxUpdatedAt?.toString());
});

onBeforeMount(() => {
  isValidSection1Importers.value = props.products.map((product) => {
    return product.sds_section1_importers.map(() => {
      return true;
    });
  });
  isValidSection1Manufacturers.value = props.products.map((product) => {
    return product.sds_section1_manufacturers.map(() => {
      return true;
    });
  });
  isValidSection2Label.value = props.products.map(() => {
    return true;
  });
  isValidSection3.value = props.products.map((product) => {
    return product.sds_section3.material_list.map(() => {
      return true;
    });
  });
  isValidSection3Impurity.value = props.products.map((product) => {
    return product.sds_section3.impurity_list.map(() => {
      return true;
    });
  });
  isValidSection8ExposureControl.value = props.products.map((product) => {
    return product.sds_section8_exposure_control_list.map(() => {
      return true;
    });
  });
  isValidSection15.value = props.products.map((product) => {
    return product.sds_section15_list.map(() => {
      return true;
    });
  });
  isValidSection9.value = props.products.map((product) => {
    return product.sds_section9_list.map(() => {
      return true;
    });
  });
});

watch(
  () => props.products,
  (next, prev) => {
    if (next.length != prev.length) {
      isValidSection1Importers.value = next.map((product) => {
        return product.sds_section1_importers.map(() => {
          return true;
        });
      });
      isValidSection1Manufacturers.value = next.map((product) => {
        return product.sds_section1_manufacturers.map(() => {
          return true;
        });
      });
      isValidSection2Label.value = props.products.map(() => {
        return true;
      });
      isValidSection3.value = next.map((product) => {
        return product.sds_section3.material_list.map(() => {
          return true;
        });
      });
      isValidSection3Impurity.value = next.map((product) => {
        return product.sds_section3.impurity_list.map(() => {
          return true;
        });
      });
      isValidSection8ExposureControl.value = next.map((product) => {
        return product.sds_section8_exposure_control_list.map(() => {
          return true;
        });
      });
      isValidSection9.value = next.map((product) => {
        return product.sds_section9_list.map(() => {
          return true;
        });
      });
      isValidSection15.value = next.map((product) => {
        return product.sds_section15_list.map(() => {
          return true;
        });
      });
    }
  },
  { deep: true }
);

watch(
  () => props.uuid,
  (newUuid, oldUuid) => {
    if (newUuid !== oldUuid) {
      const url = generateUrl(props.uuid);
      textToCopy.value = url;
    }
  }
);

watch(
  () => props.fileTags,
  () =>
    (fileTags.value = [
      ...props.fileTags.map((t) => ({ id: t.id, name: t.name })),
    ])
);

const supplierInquiryRequests = ref<SupplierInquiryResponse[]>([]);

// Prefill values for the サプライヤー問い合わせ request dialog — read from section 1
// manufacturer of the first product, matching the customer-facing note
// "SDSから取得した内容がセットされていますが、必要に応じて変更してください。"
const primaryManufacturer = computed(
  () => props.products?.[0]?.sds_section1_manufacturers?.[0] ?? null
);
const defaultSupplierName = computed(
  () => primaryManufacturer.value?.name || null
);
const defaultSupplierEmail = computed(
  () => primaryManufacturer.value?.email || null
);
const defaultSupplierPhone = computed(
  () => primaryManufacturer.value?.phone || null
);

const fetchSupplierInquiryForActiveSds = async () => {
  // Fetch every サプライヤー問い合わせ for this SDS (all statuses). Backend
  // default sort is requested_at DESC so the newest lands first, matching
  // the drawer's newest-first display order.
  try {
    const res = await generalApi.getSupplierInquiryRequests({
      sds_id: props.activeSds.uuid,
      page: 0,
      limit: 50,
    });
    supplierInquiryRequests.value = res.data.items;
  } catch {
    supplierInquiryRequests.value = [];
  }
};

watch(
  () => props.activeSds?.uuid,
  async (newUuid) => {
    if (newUuid) {
      await fetchSupplierInquiryForActiveSds();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  const url = generateUrl(props.uuid);
  textToCopy.value = url;
});

return { __sfc: true,$t, locale, pdfDisplayOptions, company, isFeatureActive, COMPANY_FEATURES, generateInactiveMessage, isSDSDigitizationCompleted, isRiskAssessmentActive, m, props, emits, hasPermission, hasSdsEditPermission, hasSdsRequestPermission, PERMISSIONS, hasGhsLabelPrintPermission, updatableValuesNum, canEdit, modalSupplierConfirmedAt, closeSupplierConfirmedAtModal, isValidSection1Manufacturers, isValidSection1Importers, isValidSection2Ghs, isValidSection2Label, isValidSection3, isValidSection3Impurity, isValidSection8, isValidSection8ExposureControl, isValidSection9, isValidSection9NewFlashPoint, isValidSection9NewBoilingPoint, isValidSection14, isValidSection15, selectedPdfDisplayDetailViewOption, fileTags, productIndex, currentProductId, riskAssessmentUrl, textToCopy, tooltipMessage, isUploading, showGhsLabelPrintDialog, onGhsLabelSuccess, generateUrl, editHistories, checkProps, section15WithoutMaterialList, section15WithMaterialList, copyText, startEdit, cancelEdit, endEdit, currentSection15, section3MaterialList, isSdsChinese, isSdsOlderThan5Years, hasAnyEmptySection8Plus, isSection8Empty, isSection9Empty, isSection14Empty, isSection15Empty, updateName, updateSupplierConfirmedAt, updateAssessedAt, showPrevSds, showNextSds, closeSdsDetail, updateProductFormItems, updateAttachments, updateProducts, updateSection1Manufacturers, updateSection1Importers, updateSection2, updateSection2Label, updateSection3, updateSection8, updateSection8ExposureControl, updateSection9, updateSection9FlashPoint, updateSection9BoilingPoint, updateUnNumber, updateUnNumberList, updateSection15WithoutMaterialList, updateSection15WithMaterialList, resetProductIndex, updateIsEditing, updateCompanySds, updatePublishedAt, updateRevisedAt, updateNote, updatePdf, updateProductCodeList, updateFileTags, updateAssigneeUsers, updateFileDepartments, exportCreateSimple, exportCreateSimpleV3, exportCreateSimpleV3_2, editDynamicListResult, addDynamicListResult, deleteDynamicListResult, pdpageScalefApp, lastCrossCheckEndedAt, lastUpdatedAt, supplierInquiryRequests, primaryManufacturer, defaultSupplierName, defaultSupplierEmail, defaultSupplierPhone, fetchSupplierInquiryForActiveSds, licenseIcon, TablerExternalLinkIcon, TablerScaleIcon, Section8ExposureControl, latest, formatDatePickerDay, PdfViewer, ReAnalysisSds, SupplierInquiryRequestButton, SdsTableSectionDefault, SdsTableSectionAccordionDefault, SdsTableSection0, SdsTableSection0DynamicList, SdsTableSection1ProductInfo, SdsTableSection14, SdsTableSection15, SdsTableSection15Material, SdsTableSection1Importer, SdsTableSection1Manufacturer, SdsTableSection2Ghs, SdsTableSection2Label, SdsTableSection3, SdsTableSection3Material, SdsTableSection3Impurity, SdsTableSection8, SdsTableSection9, SdsTableSection9BoilingPoint, SdsTableSection9FlashPoint, SdsTableSectionAccordion, SdsTableEditHistory, SdsGhsLabelSimpleDialog, sharedMessages, formatToDate }
}

})